import {
  IonBadge,
  IonButton,
  IonContent,
  IonFab,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
  type SegmentChangeEventDetail
} from "@ionic/react";
import {HomeBackButton} from "app/employee/menu/HomeBackButton";
import {MenuButton} from "app/employee/menu/MenuButton";
import {useTranslation} from "react-i18next";
import {ListPadding} from "app/employee/incdoc/ListPadding";
import {LoadingLoadable} from "app/employee/Loading";
import {type PageFunc, Pagination} from "app/employee/incdoc/Pagination";
import {updateBootstrapEmployee} from "app/employee/state/bootstrapEmployee/mutations";
import {useEmployeeClientId} from "app/employee/state/bootstrapEmployee/selectors";
import {SearchTicketsDocument, type SearchTicketsQuery, TicketDirection} from "app/gql/graphqlSchema";
import {useQuery} from "app/employee/controlCard/hook/useQuery";
import {useCallback, useMemo, useRef} from "react";
import {setTicketDirection, setTicketPageNumber, useTicketFilterStore} from "app/employee/state/ticket/store";
import {shallow} from "zustand/shallow";
import {TicketItem} from "app/employee/ticket/TicketItem";
import {addOutline, arrowRedoOutline, mailOutline, searchOutline} from "ionicons/icons";
import {ApiState} from "app/state/common/loadable";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {AddEditTicket} from "app/employee/ticket/AddEditTicket";
import {TicketFilter} from "app/employee/ticket/TicketFilter";
import type {IonSegmentCustomEvent} from "@ionic/core/dist/types/components";
import {usePollVisible} from "app/components/hooks/usePollVisible";
import {useIonViewEffect} from "app/employee/hooks/useIonViewEffect";

type SearchResult = SearchTicketsQuery["searchTickets"];

const TICKET_POLL_INTERVAL_MS = 60 * 1000;

const stateMapper = (result: SearchTicketsQuery): SearchResult => {
  updateBootstrapEmployee(result.refreshEmployee);
  return result.searchTickets;
};

const useFilterCount = () => {
  const filter = useTicketFilterStore(
    (state) => ({
      statuses: state.statuses,
      query: state.query,
      locationIds: state.locationIds
    }),
    shallow
  );

  return useMemo(() => {
    let count = 0;
    if (filter.statuses && filter.statuses.length > 0) count++;
    if (filter.query) count++;
    if (filter.locationIds && filter.locationIds.length > 0) count++;
    return count;
  }, [filter.query, filter.statuses, filter.locationIds]);
};

export const TicketListPage = () => {
  const {t} = useTranslation();
  const clientId = useEmployeeClientId();
  const addTicketModalRef = useRef<HTMLIonModalElement>(null);
  const filterModalRef = useRef<HTMLIonModalElement>(null);

  const filter = useTicketFilterStore(
    (state) => ({
      direction: state.direction,
      locationIds: state.locationIds,
      statuses: state.statuses,
      query: state.query,
      page: state.page
    }),
    shallow
  );

  const {data, refetch, fetch} = useQuery(SearchTicketsDocument, stateMapper);

  const doSearch = useCallback(async () => {
    await fetch({
      predicate: {
        clientId,
        locationIds: filter.locationIds,
        statuses: filter.statuses,
        query: filter.query,
        direction: filter.direction,
        page: filter.page
      }
    });
  }, [clientId, fetch, filter]);

  const {isPageVisible} = usePollVisible(
    refetch,
    TICKET_POLL_INTERVAL_MS,
    "Setting up periodic refresh of ticket list (employee)",
    "Stop periodic refresh of ticket list (employee)",
    "Refreshing ticket list (employee)"
  );

  useIonViewEffect(() => {
    if (isPageVisible) {
      doSearch();
    }
  }, [doSearch, isPageVisible]);

  const handleDirectionChange = (e: IonSegmentCustomEvent<SegmentChangeEventDetail>) => {
    const value = e.detail.value;
    if (value === TicketDirection.Recipient || value === TicketDirection.Sender) {
      setTicketDirection(value);
    }
  };

  const handleApplyFilter = () => filterModalRef.current?.dismiss();

  const filterCount = useFilterCount();

  const pageFunc: PageFunc = useCallback((itemCount) => t("ticket.pagination-count", {itemCount}), [t]);

  const tickets = data.value?.results;
  const page = data.value?.page;
  const noResults = data.loadingState === ApiState.OK && (tickets?.length ?? 0) === 0;

  const pagination = (
    <>
      {!noResults && (
        <IonItem lines="full">
          <IonLabel>
            <Pagination page={page} pageFunc={pageFunc} setPage={setTicketPageNumber} />
          </IonLabel>
        </IonItem>
      )}
    </>
  );

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <HomeBackButton />
          <IonTitle>{t("ticket.title")}</IonTitle>
          <MenuButton />
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <LoadingLoadable loadable={data} />
        <IonRefresher onIonRefresh={refetch} slot="fixed">
          <IonRefresherContent />
        </IonRefresher>

        <IonList>
          <IonItem color="highlight" lines="full">
            <IonLabel>{t("ticket.filter.title")}</IonLabel>
            <IonButton onClick={() => filterModalRef.current?.present()} slot="end">
              <IonIcon icon={searchOutline} slot="start" />
              {t("g.filter-button", {count: filterCount})}
            </IonButton>
          </IonItem>

          <IonItem lines="full">
            <IonSegment onIonChange={handleDirectionChange} value={filter.direction}>
              <IonSegmentButton layout="icon-top" value={TicketDirection.Recipient}>
                <IonIcon icon={mailOutline} />
                <IonLabel>
                  {t("ticket.tab.for-me")}{" "}
                  {data.value && data.value.forMeIncompleteCount > 0 && (
                    <IonBadge>{data.value.forMeIncompleteCount}</IonBadge>
                  )}
                </IonLabel>
              </IonSegmentButton>
              <IonSegmentButton layout="icon-top" value={TicketDirection.Sender}>
                <IonIcon icon={arrowRedoOutline} />
                <IonLabel>
                  {t("ticket.tab.from-me")}{" "}
                  {data.value && data.value.fromMeIncompleteCount > 0 && (
                    <IonBadge>{data.value.fromMeIncompleteCount}</IonBadge>
                  )}
                </IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </IonItem>
          {pagination}
          {tickets?.map((ticket) => (
            <TicketItem key={ticket.id.ticketId} lines="full" ticket={ticket} />
          ))}
          {noResults && (
            <IonItem lines="full">
              <IonLabel>
                <h1 className="ion-padding ion-text-center">{t("ticket.no-tickets")}</h1>
              </IonLabel>
            </IonItem>
          )}
          {pagination}
        </IonList>
        <ListPadding />
        <IonFab horizontal="end" slot="fixed" vertical="bottom">
          <IonButton onClick={() => addTicketModalRef.current?.present()} shape="round">
            <IonIcon icon={addOutline} slot="start" />
            <IonLabel>{t("ticket.new")}</IonLabel>
          </IonButton>
        </IonFab>
        <FstIonModal ref={addTicketModalRef}>
          <AddEditTicket onCompleted={doSearch} />
        </FstIonModal>
        <FstIonModal ref={filterModalRef}>
          <TicketFilter onApply={handleApplyFilter} />
        </FstIonModal>
      </IonContent>
    </>
  );
};
